import React from 'react';
import { Box, IconButton, styled, useTheme } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CustomModule } from '../ECommercePage';

const ModuleTabButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => ({
  textTransform: 'none',
  fontWeight: selected
    ? theme.typography.fontWeightMedium
    : theme.typography.fontWeightRegular,
  fontSize: theme.typography.pxToRem(14),
  padding: theme.spacing(1, 2),
  minHeight: 'auto',
  minWidth: '120px',
  color: selected ? theme.palette.text.primary : theme.palette.text.secondary,
  position: 'relative',
  backgroundColor: selected ? theme.palette.background.paper : theme.palette.grey[200],
  clipPath: 'polygon(15% 0, 85% 0, 100% 100%, 0% 100%)',
  marginRight: theme.spacing(-2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
  flexShrink: 0,
  cursor: 'pointer',
  userSelect: 'none',
  zIndex: selected ? 2 : 1,
  '&:hover': {
    backgroundColor: selected ? theme.palette.background.paper : theme.palette.grey[300],
  },
}));

interface SortableModuleTabProps {
  module: CustomModule;
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  dragHandleTitle: string;
}

const SortableModuleTab: React.FC<SortableModuleTabProps> = ({
  module,
  selected,
  onSelect,
  disabled = false,
  dragHandleTitle,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: module.id, disabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.65 : 1,
    zIndex: isDragging ? 20 : undefined,
  };

  return (
    <Box ref={setNodeRef} style={style} sx={{ flexShrink: 0 }}>
      <ModuleTabButton
        selected={selected}
        onClick={onSelect}
        aria-selected={selected}
        role="tab"
      >
        <IconButton
          component="span"
          size="small"
          title={dragHandleTitle}
          disabled={disabled}
          sx={{
            p: 0.25,
            cursor: disabled ? 'not-allowed' : 'grab',
            color: 'inherit',
            '&:active': { cursor: 'grabbing' },
          }}
          onClick={(event) => event.stopPropagation()}
          {...attributes}
          {...listeners}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Box component="span" sx={{ px: 0.5, whiteSpace: 'nowrap' }}>
          {module.tabName}
        </Box>
      </ModuleTabButton>
    </Box>
  );
};

interface SortableModuleTabsProps {
  generalLabel: string;
  activeTab: number;
  modules: CustomModule[];
  reordering: boolean;
  dragHandleTitle: string;
  fixedTabs?: Array<{ label: string }>;
  onSelectTab: (index: number) => void;
  onReorder: (modules: CustomModule[]) => void;
}

const SortableModuleTabs: React.FC<SortableModuleTabsProps> = ({
  generalLabel,
  activeTab,
  modules,
  reordering,
  dragHandleTitle,
  fixedTabs = [],
  onSelectTab,
  onReorder,
}) => {
  const theme = useTheme();
  const moduleIds = modules.map((module) => module.id);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || reordering) return;

    const oldIndex = moduleIds.indexOf(String(active.id));
    const newIndex = moduleIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    onReorder(arrayMove(modules, oldIndex, newIndex));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        backgroundColor: theme.palette.grey[200],
        '&::-webkit-scrollbar': { height: 6 },
      }}
      role="tablist"
    >
      <ModuleTabButton
        selected={activeTab === 0}
        onClick={() => onSelectTab(0)}
        aria-selected={activeTab === 0}
        role="tab"
        sx={{ pl: 3, pr: 3.5 }}
      >
        {generalLabel}
      </ModuleTabButton>

      {fixedTabs.map((tab, index) => (
        <ModuleTabButton
          key={tab.label}
          selected={activeTab === index + 1}
          onClick={() => onSelectTab(index + 1)}
          aria-selected={activeTab === index + 1}
          role="tab"
          sx={{ px: 2.5 }}
        >
          {tab.label}
        </ModuleTabButton>
      ))}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={moduleIds} strategy={horizontalListSortingStrategy}>
          {modules.map((module, index) => (
            <SortableModuleTab
              key={module.id}
              module={module}
              selected={activeTab === index + 1 + fixedTabs.length}
              onSelect={() => onSelectTab(index + 1 + fixedTabs.length)}
              disabled={reordering}
              dragHandleTitle={dragHandleTitle}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default SortableModuleTabs;
